import React, {useState, useMemo} from 'react'
import {CarouselItem, CarouselBackButton} from '@/components/ui/carousel'
import {Heading} from '@/components/ui/heading'
import {RadioGroup} from '@/components/ui/radio-group'
import PropTypes from 'prop-types'
import {Clock, Goal} from 'lucide-react'
import GoalResult from './goalResult'
import {questionPropType} from './utils'
import GoalAnswer from './goalAnswer'
import {Loader} from '@/components/loader'

const GoalQuestion = ({
    question,
    addQuestion,
    questions,
    setQuestions,
    goal,
    setGoal,
    isMobile,
    isGuest,
    isNextQuestionLoading
}) => {
    const {question: questionTitle, image, answers} = question
    const [selectedValue, setSelectedValue] = useState('')

    const isFirstQuestion = useMemo(
        () => questions[0].question == questionTitle,
        [questions, questionTitle]
    )
    const isLastSlide = useMemo(
        () => questions[questions.length - 1].question == questionTitle,
        [questions, questionTitle]
    )

    if (!answers || answers.length === 0) {
        console.error(
            'There is no data in the Learning Center Questionnaire Question "answers" field.'
        )
        return null
    }

    return (
        <CarouselItem
            className="text-primary flex justify-center gap-8 px-4 sm:px-8 py-4 items-start"
            data-testid="goalQuestion-CarouselItem"
        >
            <div className="flex-1 w-full sm:max-w-[50%]">
                {isFirstQuestion ? (
                    <div className="text-sm flex items-center space-x-2">
                        <Clock className="h-4 w-4" data-testid="goalQuestion-Clock" />{' '}
                        <span className="text-sm mt-1">Less than 1 min</span>
                    </div>
                ) : (
                    <CarouselBackButton data-testid="goalQuestion-CarouselBackButton">
                        <span className="text-sm cursor-pointer">Back</span>
                    </CarouselBackButton>
                )}
                <Heading tag="h4" variant="h4" className="my-4" data-testid="goalQuestion-Heading">
                    {questionTitle}
                </Heading>
                {isNextQuestionLoading ? (
                    <Loader data-testid="goalQuestion-Loader" />
                ) : (
                    <RadioGroup value={selectedValue} data-testid="goalQuestion-RadioGroup">
                        {answers.map((answer, answerId) => {
                            return (
                                <GoalAnswer
                                    key={`answer-${answerId}`}
                                    answer={answer}
                                    answerId={answerId}
                                    selectedValue={selectedValue}
                                    questions={questions}
                                    questionTitle={questionTitle}
                                    setGoal={setGoal}
                                    addQuestion={addQuestion}
                                    setQuestions={setQuestions}
                                    setSelectedValue={setSelectedValue}
                                    data-testid="goalQuestion-GoalAnswer"
                                />
                            )
                        })}
                    </RadioGroup>
                )}
            </div>
            <div className="hidden sm:flex flex-1 max-w-[40%] flex items-center justify-center">
                {goal && isLastSlide ? (
                    <GoalResult
                        goal={goal}
                        isMobile={isMobile}
                        isGuest={isGuest}
                        data-testid="goalQuestion-GoalResult"
                    />
                ) : (
                    <img src={image} alt={questionTitle} />
                )}
            </div>
        </CarouselItem>
    )
}

export default GoalQuestion

GoalQuestion.propTypes = {
    question: questionPropType,
    questions: PropTypes.arrayOf(questionPropType),
    goal: PropTypes.object || null,
    setGoal: PropTypes.func.isRequired,
    addQuestion: PropTypes.func.isRequired,
    setQuestions: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired,
    isGuest: PropTypes.bool.isRequired,
    isNextQuestionLoading: PropTypes.bool.isRequired
}
